import React, {useCallback, useEffect, useState} from 'react';
import {ComponentType} from 'react';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Button,
  Chip,
  Stack, Table,
} from '@mui/material';
import api from '../../../services/api';
import {DateField} from './fields/DateField';
import {TimeField} from './fields/TimeField';
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import {LoadingButton} from "@mui/lab";
import Grid from '@mui/material/Grid';
import {ConfirmationTable} from "../../../modules/service-provider/components/ConfirmationTable";
import {ExpandMoreRounded, TextsmsRounded} from "@mui/icons-material";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import EditableCell from "./EditableCell";
import SelectStaffCell from "./SelectStaffCell";
import {io} from "socket.io-client";
import {useStateContext} from "../../../hooks/useStateContext";
import Badge from "@mui/material/Badge";

interface Props {
  confirmed?: boolean;
  item: any;
  onRefresh: () => void;
  onOpenChat: () => void;
  editableFields: any;
}


export const ServiceReservationConfirmationCard: ComponentType<Props> = ({
                                                                           item,
                                                                           onRefresh,
                                                                           confirmed,
                                                                           editableFields,
                                                                           onOpenChat,
                                                                         }) => {
  const [buttonLoading, setButtonLoading] = useState<any>(false);
  const [selectedActionType, setSelectedActionType] = useState<any>(false);
  const [openOffer, setOpenOffer] = useState(false);
  const [offers, setOffers] = useState<any>([]);
  const [formValues, setFormValues] = useState<any>({});
  const [reservationIds, setReservationIds] = useState<number[]>([]);

  const onSubmit = useCallback(
    async (
      action: 'accept' | 'decline' | 'offer',
      reservationId: any,
      confirmationId: any,
      offers?: any,
    ) => {
      setButtonLoading(action);
      setSelectedActionType(action);

      const patchIfNeeded = async () => {
        if (Object.keys(formValues).length === 0) return;

        try {
          const res = await api.patch(
            `/service-provider/service-reservation-confirmations/${reservationId}/${confirmationId}`,
            formValues
          );
        } catch (error) {
          console.warn('PATCH hiba:', error);
        }
      };

      try {
        await patchIfNeeded();

        await api.post(
          `/service-provider/service-reservation-confirmations/${reservationId}/${confirmationId}`,
          {
            action,
            offers,
          }
        );

        onRefresh();
      } catch (error) {
        console.warn('POST hiba:', error);
      } finally {
        setButtonLoading(false);
      }
    },
    [formValues, onRefresh]
  );

  const handleOffer = (modification: any) => {

    let date = '2025-02-10';
    if (modification.raw_formatted?.date) {
      date = modification.formatted.date.replace(/\s*\(.*?\)/, "").replace(/\./g, "-").slice(0, -1);
    }

    let time = '11:00';
    if (modification.raw_formatted?.start_time) {
      time = modification.raw_formatted.start_time;
    }

    setOpenOffer(true);
    setOffers([{
      date: date,
      time: time,
    }])
  };

  const type = item?.formatted?.type;
  const implodedType = type.split('→');
  const isCancelled = implodedType.some((type: any) => type?.includes('Cancelled'));


  const handleFieldChange = (field: string, value: string) => {
    setFormValues((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  //console.log(item);
  const {user} = useStateContext();

  function fetchNotifications() {

    api.get('/notifications').then((result: any) => {
      const newIds = result.data
        .filter(
          (n: any) => {
            return n?.type === 'service-reservation-message' &&
              n?.service_reservation_id &&
              !reservationIds.includes(n.service_reservation_id);
          }
        )
        .map((n: any) => n.service_reservation_id);

      // ha van új id, egyszer adjuk hozzá
      if (newIds.length > 0) {
        setReservationIds((prev) => [...prev, ...newIds]);
      }
    });
  }

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    const socket = io('https://socket-zoe.stagemporium.com');
    socket.on('notification', (data:any) => {
      if ( data?.notified_user_id == user?.id && data?.data?.type === 'service-reservation-message' ) {
        const newId = data?.data?.service_reservation?.id;

        if (newId && !reservationIds.includes(newId)) {
          setReservationIds((prev) => [...prev, newId]);
        }
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  // ID törlés eseményre
  const removeReservationId = (id: number) => {
    setReservationIds((prev) => prev.filter((rId) => rId !== id));
  };

  return (
    <>
      <Card
        sx={{
          width: '100%',
          position: 'relative',
          overflow: 'visible'
        }}
      >
        <Chip
          sx={{
            position: 'absolute',
            top: '-10px',
            right: 0,
            height: '20px'
          }}
          size={'small'}
          label={<>
            <Typography variant={'caption'}>
              {item?.confirmation_status?.name}
            </Typography>
          </>}
          color={item?.confirmation_status?.id ? (item.confirmation_status.id === 1 ? 'info' : (item.confirmation_status.id === 2 ? 'success' : (item.confirmation_status.id === 3 ? 'warning' : 'error'))) : 'default'}
        />
        <CardHeader
          title={
            <>
              Group Name: <b dangerouslySetInnerHTML={{__html: item?.formatted?.group}}></b>
            </>
          }
          subheader={<>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <Typography>{item?.created_at}</Typography>
            <Tooltip  title={'Chat megnyitása'} >
              <IconButton
                size={'small'}
                onClick={() => {
                  onOpenChat();

                  removeReservationId(item?.service_reservation_id)
                }}
              >
                <Badge
                  color="error"
                  variant="dot" // pötty jelzés
                  invisible={!reservationIds.includes(item?.service_reservation_id)}
                >
                  <TextsmsRounded color="primary" />
                </Badge>
              </IconButton>
            </Tooltip>
          </Stack>
          </>}
          sx={{
            '@media (max-width:600px)': {
              p: '8px'
            },
          }}
        />
        <CardContent
          sx={{
            '@media (max-width:600px)': {
              padding: '0px 8px',
            },
          }}
        >
          <Typography variant="body1" sx={{color: 'text.secondary'}}>

            <ConfirmationTable
              item={item}
              editableFields={editableFields}
              onChange={(field, value) => {
                handleFieldChange(field, value);
              }}
            />

            {item?.assigned_staffs && (
              <SelectStaffCell
                rows={item?.assigned_staffs}
              />
            )}

          </Typography>

          {item?.history.length ? (
            <>
              <Accordion
                sx={{
                  background: '#f2f2f2'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRounded/>}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Typography variant={'body2'}>Előzmények</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack gap={1}>
                    {item.history.map((subItem: any) => {
                      return (
                        <>
                          <Card
                            sx={{
                              width: '100%',
                              position: 'relative',
                              overflow: 'visible',
                            }}
                          >
                            <Chip
                              sx={{
                                position: 'absolute',
                                top: '-10px',
                                right: 0,
                                height: '20px',
                              }}
                              size={'small'}
                              label={
                                <>
                                  <Typography variant={'caption'}>
                                    {item?.confirmation_status?.name}
                                  </Typography>
                                </>
                              }
                              color={
                                item?.confirmation_status?.id
                                  ? item.confirmation_status.id === 1
                                    ? 'info'
                                    : item.confirmation_status.id === 2
                                      ? 'success'
                                      : item.confirmation_status.id === 3
                                        ? 'warning'
                                        : 'error'
                                  : 'default'
                              }
                            />
                            <CardHeader
                              title={
                                <>
                                  Group Name: <b dangerouslySetInnerHTML={{__html: subItem?.formatted?.group}}></b>
                                </>
                              }
                              subheader={subItem?.created_at}
                              sx={{
                                '@media (max-width:600px)': {
                                  p: '8px',
                                },
                              }}
                            />
                            <CardContent
                              sx={{
                                '@media (max-width:600px)': {
                                  padding: '0px 8px',
                                },
                              }}
                            >
                              <Typography variant="body1" sx={{color: 'text.secondary'}}>

                                <ConfirmationTable
                                  item={subItem}
                                />

                              </Typography>
                            </CardContent>
                          </Card>
                        </>
                      );
                    })}

                  </Stack>
                </AccordionDetails>
              </Accordion>
            </>
          ) : (
            <>
            </>
          )}

        </CardContent>
        <CardActions
          sx={{
            justifyContent: openOffer ? 'start' : 'end',
            alignItems: 'flex-start',
          }}
        >
          {(openOffer && offers.length) ? (
            <>
              <Stack gap={1} justifyContent={'start'} alignItems={'flex-start'} width={'100%'}>
                {offers.map((offer: any, index: number) => {
                  return (
                    <>
                      <Stack
                        direction={'row'}
                        spacing={1}
                        alignContent={'start'}
                        width={'100%'}
                      >
                        <DateField
                          value={offer?.date}
                          onChange={(value: any) => {
                            setOffers((prevOffers: any[]) =>
                              prevOffers.map((prevOffer, idx) =>
                                idx === index
                                  ? {
                                    ...prevOffer,
                                    date: value,
                                  }
                                  : prevOffer
                              )
                            );
                          }}
                        />

                        <TimeField
                          value={offer?.time}
                          onChange={(value: any) => {
                            setOffers((prevOffers: any[]) =>
                              prevOffers.map((prevOffer, idx) =>
                                idx === index
                                  ? {
                                    ...prevOffer,
                                    time: value,
                                  }
                                  : prevOffer
                              )
                            );
                          }}
                        />
                      </Stack>
                    </>
                  );
                })}
                <Stack direction={'row'} justifyContent={'space-between'} width={'100%'}>
                  <Tooltip title={'Add another offer'}>
                    <IconButton color={'primary'} onClick={() => {

                      let date = '2025-02-10';
                      if (item.formatted?.date) {
                        date = item.formatted.date.replace(/\s*\(.*?\)/, "").replace(/\./g, "-").slice(0, -1);
                      }
                      if (item.formatted?.non_formatted_date) {
                        date = item.formatted.non_formatted_date.replace(/\s*\(.*?\)/, "").replace(/\./g, "-").slice(0, -1);
                      }

                      let time = '11:00';
                      if (item.formatted?.start_time) {
                        time = item.formatted.start_time;
                      }
                      if (item.formatted?.non_formatted_start_time) {
                        time = item.formatted.non_formatted_start_time;
                      }

                      setOffers((prevState: any) => [
                        ...prevState,
                        {
                          date: date,
                          time: time,
                        },
                      ]);
                    }}>
                      <AddIcon/>
                    </IconButton>
                  </Tooltip>

                  <LoadingButton
                    loading={buttonLoading === 'offer'}
                    disabled={buttonLoading}
                    variant={'contained'}
                    color={'primary'}
                    onClick={() => onSubmit('offer', item?.service_reservation_id, item?.id, offers)}
                  >Elküldöm</LoadingButton>
                </Stack>
              </Stack>
            </>
          ) : (
            <>
              {!confirmed && (
                <>
                  <Grid container spacing={1}>

                    {!isCancelled && (
                      <>
                        <Grid item xs={3} md={3} lg={12}>
                          <LoadingButton
                            fullWidth
                            variant={'outlined'}
                            color={'error'}
                            loading={buttonLoading === 'decline'}
                            disabled={buttonLoading}
                            onClick={() =>
                              onSubmit('decline', item?.service_reservation_id, item?.id)
                            }
                          >
                            Elutasítom
                          </LoadingButton>
                        </Grid>

                        <Grid item xs={5} md={5} lg={12}>
                          <Button
                            fullWidth
                            disabled={buttonLoading}
                            variant={'contained'}
                            color={'primary'}
                            onClick={() => handleOffer(item)}
                          >
                            Másik időpontot ajánlok
                          </Button>
                        </Grid>
                      </>
                    )}

                    <Grid item xs={4} md={4} lg={12}>
                      <LoadingButton
                        fullWidth
                        variant={'contained'}
                        color={'success'}
                        loading={buttonLoading === 'accept'}
                        disabled={buttonLoading}
                        onClick={() =>
                          onSubmit('accept', item?.service_reservation_id, item?.id)
                        }
                      >
                        Elfogadom
                      </LoadingButton>
                    </Grid>

                  </Grid>
                </>
              )}
            </>
          )}
        </CardActions>
      </Card>
    </>
  );
};
